import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiPhoneCall, FiCalendar, FiStar } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import { GiCrystalBall, GiPrayer } from "react-icons/gi";
import { buildWhatsAppLink } from "../utils/whatsapp";
import { SAURABH, GAURI, STATS } from "../utils/siteData";
import { useServices } from "../hooks/useServices";

export default function HeroBookingCard() {
  const { poojaServices, astrologyServices } = useServices("all");
  const [tab, setTab] = useState("pooja");

  const TABS = {
    pooja: {
      label: "Pooja",
      icon: GiPrayer,
      services: poojaServices.map((s) => s.title),
      person: SAURABH,
      intro: "Book a sacred Vedic ceremony",
    },
    astrology: {
      label: "Astrology",
      icon: GiCrystalBall,
      services: astrologyServices.map((s) => s.title),
      person: GAURI,
      intro: "Get clarity & remedies",
    },
  };

  const active = TABS[tab];

  const [service, setService] = useState("");
  const [date, setDate] = useState("");

  const message = `Namaste 🙏, I would like to book *${
    service || active.label + " service"
  }*${date ? ` around ${date}` : ""}. Please share more details.`;
  const waLink = buildWhatsAppLink(active.person.whatsapp, message);

  const rating = STATS.find((s) => s.label.includes("Rating"));

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.25 }}
      className="relative w-full max-w-md mx-auto"
    >
      {/* glow */}
      <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-gold-400/30 to-saffron-500/20 blur-xl" />

      <div className="relative glass-card rounded-3xl p-1.5">
        {/* Tabs */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-maroon-900/30">
          {Object.entries(TABS).map(([key, val]) => (
            <button
              key={key}
              onClick={() => {
                setTab(key);
                setService("");
              }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                tab === key
                  ? "bg-gradient-to-br from-gold-400 to-gold-600 text-maroon-900 shadow-gold"
                  : "text-gold-100/80 hover:text-gold-200 hover:bg-white/5"
              }`}
            >
              <val.icon size={16} /> {val.label}
            </button>
          ))}
        </div>

        <div className="p-5">
          <p className="text-gold-100/90 text-sm mb-4 flex items-center gap-2">
            <active.icon size={18} className="text-gold-300" />
            {active.intro}
          </p>

          {/* Service select */}
          <label className="block text-[11px] uppercase tracking-wider text-gold-300/80 mb-1.5 font-semibold">
            Choose a service
          </label>
          <div className="relative mb-4">
            <select
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="w-full appearance-none rounded-xl bg-white/90 text-maroon-800 px-4 py-3 pr-10 text-sm font-medium border border-gold-300/50 focus:outline-none focus:ring-2 focus:ring-gold-400 cursor-pointer"
            >
              <option value="">Select a {active.label} service</option>
              {active.services.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-maroon-500 text-xs">
              ▼
            </span>
          </div>

          {/* Date */}
          <label className="block text-[11px] uppercase tracking-wider text-gold-300/80 mb-1.5 font-semibold">
            Preferred date <span className="lowercase text-gold-300/50">(optional)</span>
          </label>
          <div className="relative mb-5">
            <FiCalendar className="absolute left-3.5 top-1/2 -translate-y-1/2 text-maroon-400" size={16} />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full rounded-xl bg-white/90 text-maroon-800 px-4 py-3 pl-10 text-sm font-medium border border-gold-300/50 focus:outline-none focus:ring-2 focus:ring-gold-400 cursor-pointer"
            />
          </div>

          {/* WhatsApp CTA */}
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3.5 rounded-xl font-semibold shadow-premium-lg transition-colors"
          >
            <FaWhatsapp size={20} /> Book on WhatsApp
          </a>

          {/* secondary row */}
          <div className="flex items-center justify-between mt-3">
            <a
              href={`tel:+91${active.person.phone}`}
              className="inline-flex items-center gap-1.5 text-gold-100/90 hover:text-gold-300 text-sm font-medium transition-colors"
            >
              <FiPhoneCall size={15} /> Call {active.person.name.split(" ")[0]}
            </a>
            {rating && (
              <span className="inline-flex items-center gap-1 text-gold-100/90 text-sm">
                <FiStar className="text-gold-300" fill="currentColor" />
                <strong className="text-gold-200">{rating.value}</strong>
                <span className="text-gold-100/60">({rating.suffix || ""}500+)</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
